import { z } from 'zod';

export const QuakeSchema = z.object({
  timestamp: z.iso.datetime().optional().default(() => new Date().toISOString()),
  window: z.string().default('5m'),
  window_seconds: z.number().optional().default(300),
  asset_id: z.string(),
  market_id: z.string().optional().default(''),
  question: z.string(),
  event_slug: z.string().optional().default(''),
  category: z.string().optional().default(''),
  outcome: z.string().optional().default('YES'),
  old_price: z.number(),
  new_price: z.number(),
  change_pp: z.number(),
  velocity_pps: z.number().optional().default(0),
  spread: z.number().optional().default(0),
  depth_near_mid: z.number().optional().default(0),
  window_volume: z.number(),
  baseline_volume: z.number(),
  volume_spike_ratio: z.number(),
  capital_weighted_move: z.number(),
  activity_scale: z.number(),
  open_interest: z.number(),
  dynamic_min_volume: z.number(),
  dynamic_min_capital_weighted_move: z.number(),
  directional_persistence: z.number(),
  baseline_ready: z.boolean(),
  score_spike: z.number().optional().default(0),
  score_capital: z.number().optional().default(0),
  score_move: z.number().optional().default(0),
  score_spread: z.number().optional().default(0),
  score_depth: z.number().optional().default(0),
  score: z.number().optional().default(0),
  reliability: z.number().optional().default(0),
  signal: z.string().optional().default('capital confirmed'),
  direction: z.string().optional().default('flat'),
});

export type Quake = z.infer<typeof QuakeSchema>;

export const StatsSchema = z.object({
  markets: z.number().optional().default(0),
  assets: z.number().optional().default(0),
  active_assets: z.number().optional().default(0),
  resolved_assets: z.number().optional().default(0),
  messages: z.number().optional().default(0),
  book_messages: z.number().optional().default(0),
  price_messages: z.number().optional().default(0),
  trade_messages: z.number().optional().default(0),
  best_bid_ask_messages: z.number().optional().default(0),
  unknown_messages: z.number().optional().default(0),
  last_discovery_at: z.string().optional(),
  last_message_at: z.string().optional(),
  // Scoring-attempt counters (per poll, per asset). NOT unique
  // candidate events. See engine docs.
  scoring_attempts: z.number().optional().default(0),
  confirmed: z.number().optional().default(0),
  watchlist: z.number().optional().default(0),
  median_heat: z.number().optional().default(0),
  median_reliability: z.number().optional().default(0),
});

export type Stats = z.infer<typeof StatsSchema>;

export const SummarySchema = z.object({
  active_markets: z.number().optional().default(0),
  active_assets: z.number().optional().default(0),
  quake_events_today: z.number().optional().default(0),
  avg_move_5m_pp: z.number().optional().default(0),
  markets_with_catalyst_match_pct: z.number().optional().default(0),
  live_streams_connected: z.number().optional().default(0),
  last_discovery_at: z.string().optional().default(''),
  last_message_at: z.string().optional().default(''),
});

export type Summary = z.infer<typeof SummarySchema>;

export const TokenSchema = z.object({
  asset_id: z.string(),
  outcome: z.string(),
});

export type Token = z.infer<typeof TokenSchema>;

export const MarketSchema = z.object({
  id: z.string(),
  condition_id: z.string().optional().default(''),
  event_id: z.string().optional().default(''),
  series_id: z.string().optional().default(''),
  question: z.string(),
  event_slug: z.string().optional().default(''),
  market_slug: z.string().optional().default(''),
  category: z.string().optional().default(''),
  tags: z.array(z.string()).optional().default([]),
  end_time: z.string().optional().default(''),
  volume_24h: z.number().optional().default(0),
  liquidity: z.number().optional().default(0),
  open_interest: z.number().optional().default(0),
  active: z.boolean().optional().default(false),
  closed: z.boolean().optional().default(false),
  tokens: z.array(TokenSchema).optional().default([]),
  updated_at: z.string().optional().default(''),
});

export type Market = z.infer<typeof MarketSchema>;

export const MarketsResponseSchema = z.object({
  items: z.array(MarketSchema).default([]),
});

export type MarketsResponse = z.infer<typeof MarketsResponseSchema>;

export const HealthSchema = z.object({
  ok: z.boolean().optional().default(false),
  stats: StatsSchema.optional(),
});

export type Health = z.infer<typeof HealthSchema>;

export const MoversResponseSchema = z.object({
  window: z.string().default('5m'),
  items: z.array(QuakeSchema).default([]),
});

export type MoversResponse = z.infer<typeof MoversResponseSchema>;

export const QuakeEventSchema = z.object({
  id: z.string(),
  market_id: z.string(),
  asset_id: z.string(),
  question: z.string(),
  event_slug: z.string(),
  category: z.string(),
  outcome: z.string(),
  window: z.string(),
  direction: z.string(),
  old_price: z.number(),
  new_price: z.number(),
  change_pp: z.number(),
  velocity_pps: z.number(),
  spread: z.number(),
  depth_near_mid: z.number(),
  window_volume: z.number(),
  baseline_volume: z.number(),
  volume_spike_ratio: z.number(),
  capital_weighted_move: z.number(),
  activity_scale: z.number(),
  open_interest: z.number(),
  dynamic_min_volume: z.number(),
  dynamic_min_capital_weighted_move: z.number(),
  directional_persistence: z.number(),
  baseline_ready: z.boolean(),
  score_spike: z.number().optional().default(0),
  score_capital: z.number().optional().default(0),
  score_move: z.number().optional().default(0),
  score_spread: z.number().optional().default(0),
  score_depth: z.number().optional().default(0),
  score: z.number(),
  reliability: z.number().optional().default(0),
  peak_score: z.number(),
  signal: z.string(),
  first_seen_at: z.string(),
  last_seen_at: z.string(),
  active_until: z.string(),
});

export type QuakeEvent = z.infer<typeof QuakeEventSchema>;

export const RecentQuakeEventsResponseSchema = z.object({
  items: z.array(QuakeEventSchema).default([]),
});

export type RecentQuakeEventsResponse = z.infer<typeof RecentQuakeEventsResponseSchema>;

export const TodayQuakeEventsResponseSchema = z.object({
  items: z.array(QuakeEventSchema).default([]),
  count: z.number().default(0),
});

export type TodayQuakeEventsResponse = z.infer<typeof TodayQuakeEventsResponseSchema>;

export const RelatedMarketSchema = z.object({
  market_id: z.string(),
  related_market_id: z.string(),
  question: z.string(),
  event_slug: z.string(),
  category: z.string(),
  outcome: z.string(),
  reason: z.string(),
  static_score: z.number(),
  correlation: z.number().nullable().optional(),
  move_pp: z.number(),
  old_price: z.number(),
  new_price: z.number(),
  score: z.number(),
  updated_at: z.string(),
});

export type RelatedMarket = z.infer<typeof RelatedMarketSchema>;

export const RelatedMarketsResponseSchema = z.object({
  market_id: z.string(),
  window: z.string(),
  items: z.array(RelatedMarketSchema).default([]),
});

export type RelatedMarketsResponse = z.infer<typeof RelatedMarketsResponseSchema>;

export const CatalystEventSchema = z.object({
  id: z.string(),
  quake_event_id: z.string().optional(),
  market_id: z.string(),
  question: z.string().optional().default(''),
  event_slug: z.string().optional().default(''),
  category: z.string().optional().default(''),
  polymarket_url: z.string().optional().default(''),
  quake_change_pp: z.number().optional(),
  quake_score: z.number().optional(),
  quake_direction: z.string().optional().default(''),
  source: z.enum(['related_market_cluster', 'timing_boundary', 'liquidity_warning', 'external_news']).or(z.string()),
  label: z.string(),
  title: z.string(),
  summary: z.string(),
  confidence: z.number(),
  observed_at: z.string(),
  payload: z.unknown().optional(),
});

export type CatalystEvent = z.infer<typeof CatalystEventSchema>;

export const ExternalNewsPayloadSchema = z.object({
  provider: z.string().optional().default(''),
  url: z.string().optional().default(''),
  source: z.string().optional().default(''),
  published_at: z.string().optional().default(''),
  snippet: z.string().optional().default(''),
  reasons: z.array(z.string()).optional().default([]),
});

export type ExternalNewsPayload = z.infer<typeof ExternalNewsPayloadSchema>;

export const RecentCatalystEventsResponseSchema = z.object({
  items: z.array(CatalystEventSchema).default([]),
});

export type RecentCatalystEventsResponse = z.infer<typeof RecentCatalystEventsResponseSchema>;

export const MarketCatalystEventsResponseSchema = z.object({
  market_id: z.string(),
  items: z.array(CatalystEventSchema).default([]),
});

export type MarketCatalystEventsResponse = z.infer<typeof MarketCatalystEventsResponseSchema>;

export const LiveMoversMessageSchema = z.object({
  type: z.literal('top_movers'),
  timestamp: z.string().or(z.date()).transform((value) =>
    value instanceof Date ? value.toISOString() : value,
  ),
  window: z.string(),
  items: z.array(QuakeSchema),
  stats: StatsSchema.optional(),
});

export type LiveMoversMessage = z.infer<typeof LiveMoversMessageSchema>;
